import type { NodeParameters } from '../../../types/node-properties';

/**
 * Validate Data Table node parameters
 */
export function validate(params: NodeParameters): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const operation = params.operation as string;

  // For createTable operation, tableId is not required
  if (operation === 'createTable') {
    if (!params.newTableName) {
      errors.push('Table name is required');
    }
    if (!params.newTableSchema) {
      errors.push('Schema is required');
    } else {
      // Validate schema is valid JSON
      try {
        const schema = JSON.parse(params.newTableSchema as string);
        if (!schema.columns || !Array.isArray(schema.columns) || schema.columns.length === 0) {
          errors.push('Schema must have at least one column');
        }
      } catch {
        errors.push('Schema must be valid JSON');
      }
    }
    return { valid: errors.length === 0, errors };
  }

  // Check for createIfNotExists mode
  const createIfNotExists = params.createIfNotExists as boolean;
  const isWriteOperation = ['insert', 'update', 'upsert', 'append', 'replaceAll', 'bulkUpdate'].includes(operation);

  if (createIfNotExists && isWriteOperation) {
    // In createIfNotExists mode, require newTableNameForCreate instead of tableId
    if (!params.newTableNameForCreate) {
      errors.push('Table name is required when "Create if not exists" is enabled');
    }
  } else {
    // Table ID is required for all other operations
    if (!params.tableId) {
      errors.push('Please select a data table');
    }
  }

  // Validate based on operation
  switch (operation) {
    case 'getById':
      if (!params.rowId) {
        errors.push('Row ID is required');
      }
      break;

    case 'query':
      if (!params.query) {
        errors.push('SQL query is required');
      }
      break;

    case 'insert':
    case 'update':
      if (!params.data) {
        errors.push('Row data is required');
      }
      if (operation === 'update' && !params.rowIdForUpdate) {
        errors.push('Row ID is required for update');
      }
      break;

    case 'upsert':
      if (!params.data) {
        errors.push('Row data is required');
      }
      if (!params.upsertKey) {
        errors.push('Key field is required for upsert');
      }
      break;

    case 'delete':
      if (!params.rowIdForUpdate) {
        errors.push('Row ID is required for delete');
      }
      break;

    case 'append':
    case 'replaceAll':
      if (!params.rows) {
        errors.push('Rows data is required');
      }
      if (operation === 'replaceAll' && !params.confirmReplaceAll) {
        errors.push('Please confirm the replace all operation');
      }
      break;

    case 'bulkUpdate':
      if (!params.bulkCondition) {
        errors.push('Condition is required for bulk update');
      }
      if (!params.bulkUpdateData) {
        errors.push('Update data is required');
      }
      break;

    case 'bulkDelete':
      if (!params.bulkCondition) {
        errors.push('Condition is required for bulk delete');
      }
      break;
  }

  return { valid: errors.length === 0, errors };
}

export default { validate };
